import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Stack,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from '../utils/axios';

const SubscriptionPlans = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(t('yoga'));
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const categories = [t('yoga'), t('soin'), t('ateliers'), t('autres')];

  useEffect(() => {
    fetchPlans();
  }, [selectedCategory]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/plans/public${selectedCategory ? `?category=${selectedCategory}` : ''}`);
      setPlans(response.data);
      setError(null);
    } catch (error) {
      setError(t('errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = (plan) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/subscriptions' } });
      return;
    }
    setSelectedPlan(plan);
    setOpenDialog(true);
  };

  const handleConfirmSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`/api/subscriptions/request/${selectedPlan._id}`);
      
      setSuccess(response.data.message);
      setOpenDialog(false);
      setSelectedPlan(null);
    } catch (err) {
      setError(err.response?.data?.message || t('errorSaving'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlan(null);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
  };

  const formatDuration = (minutes) => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return `${hours}${t('hours')}${remainingMinutes ? ` ${remainingMinutes}${t('minutes')}` : ''}`;
    }
    return `${minutes} ${t('minutes')}`;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box textAlign="center" mb={8}>
        <Typography variant="h2" component="h1" gutterBottom>
          {t('choosePlan')}
        </Typography>
        <Typography variant="h5" color="text.secondary" mb={4}>
          {t('selectPlanMessage')}
        </Typography>

        {/* Category Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 4,
          width: '100%',
          overflow: 'hidden'
        }}>
          <Tabs
            value={selectedCategory}
            onChange={handleCategoryChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={t('categories')}
            sx={{
              '& .MuiTabs-flexContainer': {
                gap: { xs: 0.5, sm: 1 }
              },
              '& .MuiTab-root': {
                fontSize: { xs: '0.875rem', sm: '1rem' },
                minWidth: { xs: 'auto', sm: 120 },
                padding: { xs: '6px 12px', sm: '12px 16px' },
                minHeight: { xs: '48px', sm: '56px' },
                textTransform: 'none',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600
                }
              },
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            {categories.map((category) => (
              <Tab
                key={category}
                label={category}
                value={category}
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Success Message */}
      {success && (
        <Alert severity="success" sx={{ mb: 4 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        /* Pricing Cards */
        <Grid container spacing={4} alignItems="stretch">
          {plans.map((plan) => (
            <Grid item key={plan._id} xs={12} md={4}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                  },
                }}
              >
                <CardHeader
                  title={plan.name}
                  subheader={formatDuration(plan.duration)}
                  titleTypographyProps={{ align: 'center', variant: 'h4' }}
                  subheaderTypographyProps={{ align: 'center', sx: { mb: 2 } }}
                  sx={{ pt: 4, pb: 2 }}
                />
                <CardContent sx={{ flexGrow: 1, pt: 0 }}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="h3" color="primary">
                      {plan.price}€
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {plan.validityPeriod} {t('months')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                    dangerouslySetInnerHTML={{ __html: plan.description }}
                  />
                  <Stack spacing={2} alignItems="center">
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{t('confirmSubscription')}</DialogTitle>
        <DialogContent>
          {selectedPlan && (
            <>
              <DialogContentText>
                {t('confirmSubscriptionMessage')}
              </DialogContentText>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('planName')}: {selectedPlan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {selectedPlan.description}
                </Typography>
                <Typography variant="body2">
                  {t('price')}: {selectedPlan.price}€
                </Typography>
                <Typography variant="body2">
                  {t('validityPeriod')}: {selectedPlan.validityPeriod} {t('months')}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('cancel')}</Button>
          <Button 
            onClick={handleConfirmSubscription} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? t('loading') : t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SubscriptionPlans; 